import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { FormikHelpers } from 'formik';
import {
  getApplicationErrorMessage,
  getTokenErrorMessage,
  isExpiredTokenError,
  isNetworkError,
} from 'modules/application-link/domain/errors/application-error-messages';
import { TokenData } from 'modules/application-link/domain/interfaces/token-data';
import applicationsResource from 'modules/application-link/domain/resources/applications.resource';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import UDText from 'ud-ui/components/text';
import ApplicationLinkForm, { IFormValues } from '../components/application-link-form';
import ApplicationNotice from '../components/application-notice';
import Layout from '../components/layout';
import ApplicationExpiredPage from './application-expired-page';
import { theme } from 'styles/theme/theme';

const ApplicationLinkPage: React.FC = () => {
  const { t } = useLingui();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const {
    data: tokenData,
    isLoading,
    error,
  } = useQuery<TokenData | undefined>({
    queryKey: ['token', token],
    queryFn: () => applicationsResource.decodeToken(token as string),
    enabled: Boolean(token),
    retry: false,
    // Токен в адресе не меняется, а перезапрашивать его опасно: на телефоне возврат
    // из системного выбора фото возвращает вкладке фокус, и обновление по фокусу
    // на дефолтах react-query снесло бы форму вместе со всем, что человек ввёл.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const submit = async (values: IFormValues, formikHelpers: FormikHelpers<IFormValues>) => {
    try {
      if (!token) {
        toast.error(t`Ссылка неполная. Запросите новую у организатора`);
        return;
      }

      const photo = values.photo?.[0] instanceof File ? (values.photo[0] as File) : undefined;

      await applicationsResource.createFromToken({
        name: values.name,
        surname: values.surname,
        middleName: values.patronymic || undefined,
        // Бэкенд принимает дату только как dd.mm.yyyy — `Util/DateValidator.php`
        // на ISO отвечает 500. Значение уходит строкой формы, без нормализации.
        birthDate: values.birthday,
        token: token,
        number: Number(values.number),
        position: values.position?.value as string,
        photo,
      });

      toast.success(t`Заявка отправлена`);
      navigate(Pages.APPLICATION_LINK.SUCCESS);
    } catch (error) {
      const { message, applicationCreated } = getApplicationErrorMessage(error);

      // Заявка создаётся и коммитится до загрузки фото в хранилище, поэтому падение
      // на фото оставляет её существовать. Предлагать отправить ещё раз нельзя —
      // человек создаст дубль.
      if (applicationCreated) {
        toast.warning(message);
        navigate(Pages.APPLICATION_LINK.SUCCESS);
        return;
      }

      toast.error(message);
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  // Форму показываем, только когда с токеном всё в порядке. Если он сломан, просрочен
  // или его не удалось проверить, отправка всё равно не пройдёт — а человек успеет
  // заполнить все поля, прежде чем это выяснится.
  if (!token) {
    return <ApplicationNotice text={t`Ссылка неполная. Запросите новую ссылку у организатора — токен не найден.`} emoji="🔗" />;
  }

  const expiredMilliseconds = tokenData?.exp ? tokenData.exp * 1000 : undefined;
  if ((expiredMilliseconds && expiredMilliseconds < Date.now()) || isExpiredTokenError(error)) {
    return <ApplicationExpiredPage />;
  }

  if (error) {
    // При обрыве связи ссылка, скорее всего, цела — отсюда другой значок и другой
    // текст: проблему можно исправить и открыть ссылку заново.
    return <ApplicationNotice text={getTokenErrorMessage(error)} emoji={isNetworkError(error) ? '📡' : '🔗'} />;
  }

  return (
    <Layout>
      <UDText id="title" type="title-1" style={{ fontWeight: 700 }} color="white" className="mb-9">
        <Trans>Онлайн заявка на участие в турнире</Trans>
      </UDText>
      {
        <div id="organizer" className="justify-content: center mb-5 flex flex-row items-center gap-3">
          {isLoading ? (
            <SkeletonBlock className="skeleton-block" style={{ width: 220, height: 54, borderRadius: 8 }} />
          ) : (
            <>
              <img
                alt="organizer-logo"
                src={tokenData?.tournamentLogoUrl ?? USER_DEFAULT_AVATAR}
                id="organizer-logo"
                style={{
                  border: `1px solid ${theme.colors.SurfaceRomanSilver30}`,
                  width: 54,
                  height: 54,
                  objectFit: 'contain',
                  borderRadius: 8,
                }}
              />
              <UDText id="organizer-name" type="title-1" style={{ fontWeight: 500, fontSize: 26 }} color="white">
                {tokenData?.tournamentName ?? t`Неизвестный организатор`}
              </UDText>
            </>
          )}
        </div>
      }
      <div id="tournament" className="mb-5 flex flex-col items-center justify-center gap-2">
        <UDText id="tournament-title" type="subhead" style={{ fontWeight: 400 }} color={theme.colors.SurfaceRomanSilver30}>
          <Trans>Турнир</Trans>
        </UDText>
        {isLoading ? (
          <SkeletonBlock className="skeleton-block" style={{ width: 220, height: 34, borderRadius: 8 }} />
        ) : (
          <UDText id="tournament-name" type="title-1" style={{ fontWeight: 500, fontSize: 20 }} color="white">
            {tokenData?.championshipName ?? t`Неизвестный турнир`}
          </UDText>
        )}
      </div>
      <div id="team" className="mb-7 flex flex-col items-center justify-center gap-2">
        <UDText id="team-title" type="subhead" style={{ fontWeight: 400 }} color={theme.colors.SurfaceRomanSilver30}>
          <Trans>Ваша команда</Trans>
        </UDText>
        {isLoading ? (
          <SkeletonBlock className="skeleton-block" style={{ width: 220, height: 34, borderRadius: 8 }} />
        ) : (
          <UDText id="team-name" type="title-1" style={{ fontWeight: 500, fontSize: 20 }} color="white">
            {tokenData?.teamName ?? t`Неизвестная команда`}
          </UDText>
        )}
      </div>
      <ApplicationLinkForm onSubmit={submit} />
    </Layout>
  );
};

export default ApplicationLinkPage;

export const SkeletonBlock = styled.div`
  width: 100%;
  height: 20px;

  background: linear-gradient(110deg, #4b4b68 3%, #25254e 60%, #4b4b68 97%);
  background-size: 200% 100%;
`;
